import { useShowsCena } from "@/shared/components/cena-visibility";
import type { Mention } from "@/shared/types/mention";
import { formatCena } from "@/shared/utils/format";
import { cenaBarPercent, cenaState, cenaTone } from "./mention-cena-state";

/**
 * O selo da nota CENA no topo da coluna de classificação do card (2026-10-22):
 * a nota à vista, sem abrir "Mais detalhes". É um botão que abre/fecha esses
 * detalhes, onde a nota por ponto já aparece. As regras de quando há nota,
 * quando ela é do relatório e quando falta são as de `cenaState`, as mesmas
 * do resumo do card expandido.
 */
export function MentionCenaBadge({
  mention,
  onToggleExpanded
}: {
  mention: Mention;
  onToggleExpanded: () => void;
}) {
  const state = cenaState(mention, useShowsCena());
  if (state.kind === "hidden") return null;

  if (state.kind === "scored") {
    const shown = formatCena(state.value);
    return (
      <button
        type="button"
        className={`mc-cena is-${cenaTone(state.value)}`}
        aria-label={`Nota CENA ${shown} de 10 — ver os pontos`}
        title="Ver a nota por ponto"
        onClick={onToggleExpanded}
      >
        <span className="mc-cena-line">
          <span className="mc-cena-label">CENA</span>
          <strong className="mc-cena-value">{shown}</strong>
        </span>
        <span className="mc-cena-bar" aria-hidden="true">
          <i style={{ width: `${cenaBarPercent(state.value)}%` }} />
        </span>
      </button>
    );
  }

  const text =
    state.kind === "pending" ? "CENA na amostra" : state.kind === "unscored" ? "Sem nota" : "CENA —";
  const title =
    state.kind === "pending"
      ? "Posts de rede têm a nota calculada na amostra (depende do percentil do período)."
      : state.kind === "unscored"
        ? "Menção neutra, e o peso do neutro não está cadastrado."
        : "Esta menção ainda não tem nota CENA.";
  return (
    <button
      type="button"
      className="mc-cena is-muted"
      aria-label={`${text} — ver os detalhes`}
      title={title}
      onClick={onToggleExpanded}
    >
      <span className="mc-cena-line">{text}</span>
    </button>
  );
}
